import type { Category, CollectionViewMode, Todo } from "../domain/entities.ts";
import type { AppView, SavedView } from "../domain/app-types.ts";
import type { ShellData } from "./shell-data-controller.ts";

export interface ShellTaskDataCapabilities {
  load(): Promise<ShellData>;
  apply(data: ShellData): void;
  replaceSavedViews(views: SavedView[]): void;
  applyScheduledBoardAutomations(todos: Todo[], categories: Category[]): Promise<boolean>;
  markLocal(): void;
  queueSync(): void;
  activeCollection(): string | null;
  activeRoot(): string | null;
  collectionView(category: Category): CollectionViewMode;
  view(): AppView;
  selectionActive(): boolean;
  clearSelection(): void;
  retainSelection(ids: string[]): void;
  canEdit(id?: string): boolean;
  matchesBoard(todo: Todo): boolean;
  matchesTimeline(todo: Todo): boolean;
  matches(todo: Todo): boolean;
  draft(): Todo | null;
  recoveredEditDraft(): Todo | null;
  editing(): boolean;
  selectedId(): string | null;
}

export interface PreparedShellTaskData {
  todos: Todo[];
  allCategories: Category[];
  boardCategory: Category | null;
  timelineCategory: Category | null;
  visible: Todo[];
  selected: Todo | undefined;
  canCreateTasks: boolean;
}

export function createShellTaskDataRuntime(capabilities: ShellTaskDataCapabilities) {
  async function prepare(): Promise<PreparedShellTaskData> {
    const data = await capabilities.load();
    const { todos } = data;
    capabilities.apply(data);
    capabilities.replaceSavedViews(data.savedViews);
    if (await capabilities.applyScheduledBoardAutomations(todos, data.indexedCategories)) {
      capabilities.markLocal();
      capabilities.queueSync();
    }

    const activeCategory = data.categories.find(category => category.id === capabilities.activeCollection()) || null;
    const selectedView = activeCategory && capabilities.view() === "open" ? capabilities.collectionView(activeCategory) : null;
    const boardCategory = selectedView === "board" ? activeCategory : null;
    const timelineCategory = selectedView === "timeline" ? activeCategory : null;
    if (capabilities.selectionActive() && (capabilities.view() === "calendar" || timelineCategory)) capabilities.clearSelection();
    const visible = todos.filter(todo => boardCategory ? capabilities.matchesBoard(todo) : timelineCategory ? capabilities.matchesTimeline(todo) : capabilities.matches(todo));
    capabilities.retainSelection(todos.filter(todo => !todo.deleted && capabilities.canEdit(todo.id)).map(todo => todo.id));
    const selected = capabilities.draft() || (capabilities.editing() ? capabilities.recoveredEditDraft() : null) || todos.find(todo => todo.id === capabilities.selectedId());
    const canCreateTasks = !!capabilities.activeRoot() && capabilities.canEdit();
    return { todos, allCategories: data.allCategories, boardCategory, timelineCategory, visible, selected, canCreateTasks };
  }

  return { prepare };
}
